import { ReactNode } from "react";
import { Contract, Order, WeightUnit } from "@/types";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import Text from "@/components/atoms/Text";
import Accordion from "@/components/organisms/AccordionDrawer";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import styles from "./styles.module.css";
import { useAuth } from "@/contexts/AuthContext";
import { useGetContractById } from "@/services/Contracts";
import TextInput from "@/components/atoms/TextInput";
import CTA from "@/components/atoms/CTA";
import { useGetOpenTasks } from "@/services/Tasks";
import {
  FinalAmountFormInput,
  FinalAmountValidationSchema,
  WeightFinalAmountFormInput,
  WeightFinalAmountValidationSchema,
} from "@/components/pages/Tasks/validation/finalAmountValidation";
import WeightInput from "@/components/molecules/WeightInput";
import FinalAmountWarningModal from "@/components/organisms/FinalAmountWarningModal";
import {
  FinalQuantityEntry,
  useFinalQuantitySave,
} from "@/components/organisms/FinalAmountWarningModal/useFinalQuantitySave";
import { weightUnitOf } from "@/utils/weight";
import QuantityCheckValue from "@/components/molecules/DrawerOrders/QuantityCheckValue";

interface FinalAmountAccordionProps {
  order: Order;
}

export default function FinalAmountAccordion({ order }: FinalAmountAccordionProps) {
  const { currentCompany } = useAuth();
  const { data: contract } = useGetContractById(order.contractId as number, {
    enabled: !!order.contractId,
  });

  const { data: tasks } = useGetOpenTasks(currentCompany?.companyId as number, "FINAL_AMOUNT", {
    enabled: !!currentCompany?.companyId,
  });
  const taskId = tasks?.find((task) => task.connected === order.id)?.id;
  const hasFinalAmountTask = taskId !== undefined;

  // The save owns the notifications, the cache refresh and the backend's plausibility warning. Once it has saved, the
  // refreshed open tasks no longer hold this order's task, which hides the input.
  const { save, isPending, fieldErrors, warningModal } = useFinalQuantitySave({
    mode: "single",
    taskId,
    close: () => {},
  });

  const weightResponsible = contract ? findWeightResponsible(contract) : null;

  const frame: FrameProps = {
    order,
    weightResponsibleName: weightResponsible?.companyName,
    isWeightResponsible: weightResponsible?.id === currentCompany?.companyId,
    hasFinalAmountTask,
  };

  // Orders counted in kg or tonnes get the weight field; any other unit keeps the plain amount field.
  const weightUnit = weightUnitOf(order.unit?.unit);

  return (
    <>
      {weightUnit ? (
        <WeightAccordion
          frame={frame}
          weightUnit={weightUnit}
          save={save}
          isPending={isPending}
          saveError={fieldErrors[order.id]}
        />
      ) : (
        <AmountAccordion frame={frame} save={save} isPending={isPending} saveError={fieldErrors[order.id]} />
      )}
      <FinalAmountWarningModal {...warningModal} />
    </>
  );
}

const findWeightResponsible = (contract: Contract) => {
  switch (contract.weightResponsible.toUpperCase()) {
    case "SELLER":
      return contract.contractSellers[0];
    case "BUYER":
      return contract.contractBuyers[0];
    default:
      return null;
  }
};

interface FrameProps {
  order: Order;
  weightResponsibleName?: string;
  isWeightResponsible: boolean;
  hasFinalAmountTask: boolean;
}

interface SaveProps {
  save: (entries: FinalQuantityEntry[]) => void;
  isPending: boolean;
  /** Why the last save of this order failed, as a translation key. A validation message on the field goes first. */
  saveError?: string;
}

interface WeightAccordionProps extends SaveProps {
  frame: FrameProps;
  weightUnit: WeightUnit;
}

/** The accordion of a kg or tonnes order: the weight is sent as typed, and the backend decides whether it is plausible. */
const WeightAccordion = ({ frame, weightUnit, save, isPending, saveError }: WeightAccordionProps) => {
  const { t } = useTranslation();
  const { order } = frame;

  // The form lives here, above the accordion, so what is typed survives closing and opening it.
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<WeightFinalAmountFormInput>({
    resolver: zodResolver(WeightFinalAmountValidationSchema),
    // The weight field has no blur event, so the form is checked as the weight is typed.
    mode: "onChange",
    values: {
      weight: { amount: order.amount || "", unit: weightUnit },
    },
  });

  const onSubmit = ({ weight }: WeightFinalAmountFormInput) => save([{ orderId: order.id, weight }]);

  return (
    <AccordionFrame {...frame}>
      <Grid.Col span={12}>
        <form id="final-amount-form" onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
          <Controller
            control={control}
            name="weight"
            render={({ field, fieldState }) => (
              <WeightInput
                data-testid="final-amount-input"
                label={t("common:amount")}
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error ? t(fieldState.error.message as string) : saveError && t(saveError)}
              />
            )}
          />
          <CTA type="submit" intent="secondary" mt={10} disabled={!isValid || isPending}>
            {t("actions:save")}
          </CTA>
        </form>
      </Grid.Col>
    </AccordionFrame>
  );
};

/** The accordion of an order in another unit (stk, m3, meter): the plain amount field, held to the agreed deviation. */
const AmountAccordion = ({ frame, save, isPending, saveError }: SaveProps & { frame: FrameProps }) => {
  const { t } = useTranslation();
  const { order } = frame;

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setError,
  } = useForm<FinalAmountFormInput>({
    resolver: zodResolver(FinalAmountValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      amount: order?.amount || 0,
    },
  });

  const onSubmit = (data: FinalAmountFormInput) => {
    const amount = data.amount;
    const originalAmount = order.amount;
    const amountDeviation = order.amountDeviation || 0;

    const deviation: number = (originalAmount / 100) * amountDeviation;
    const lowEnd: number = originalAmount - deviation;
    const highEnd: number = originalAmount + deviation;

    if (amount > highEnd || amount < lowEnd) {
      setError(`amount`, {
        type: "manual",
        message: t("tasks:legalFinalAmount") + lowEnd + order.unit?.unit + " - " + highEnd + order.unit?.unit,
      });
      return;
    }

    save([{ orderId: order.id, amount }]);
  };

  return (
    <AccordionFrame {...frame}>
      <Grid.Col span={6}>
        <form id="final-amount-form" onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
          <Controller
            control={control}
            name={"amount"}
            render={({ field, fieldState }) => (
              <TextInput
                data-testid="final-amount-input"
                label={t("common:amount") + " (" + order?.unit?.unit + ")"}
                value={field.value === undefined ? 0 : field.value}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || value === undefined) {
                    field.onChange(0);
                    return;
                  }
                  const numValue = parseFloat(value);
                  field.onChange(isNaN(numValue) ? 0 : numValue);
                }}
                onBlur={field.onBlur}
                error={
                  fieldState.error
                    ? fieldState.error.type === "manual"
                      ? fieldState.error.message
                      : t(fieldState.error.message as string)
                    : saveError && t(saveError)
                }
              />
            )}
          />
          <CTA type="submit" intent="secondary" mt={10} disabled={!isValid || isPending}>
            {t("actions:save")}
          </CTA>
        </form>
      </Grid.Col>
    </AccordionFrame>
  );
};

interface AccordionFrameProps extends FrameProps {
  /** The final amount form, shown to the weight responsible company while the order has a final amount task. */
  children: ReactNode;
}

/** The accordion around the final amount form: who is weight responsible and what is left to do. */
const AccordionFrame = ({
  order,
  weightResponsibleName,
  isWeightResponsible,
  hasFinalAmountTask,
  children,
}: AccordionFrameProps) => {
  const { t } = useTranslation();

  return (
    <Accordion title={t("orders:finalAmount")}>
      <Grid className={styles.grid}>
        <Grid.Col span={5}>
          <Text size="sm" color={"secondary"}>
            {t("contracts:weightResponsible")}
          </Text>
        </Grid.Col>

        <Grid.Col span={7}>
          <Text size="sm" color={"primary"}>
            {weightResponsibleName}
          </Text>
        </Grid.Col>

        {/*
          <Grid.Col span={5}>
            <Text size="sm" color={"secondary"}>
              {t("orders:finalAmount")}
            </Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Text size="sm" color={"primary"}>
              {order.finalAmount && order.finalAmount + " " + order.unit?.unit}
            </Text>
          </Grid.Col>
          */}

        <Grid.Col span={10}>
          <Text size="sm" color={"secondary"}>
            {t("orders:finalAmountInfo")}
          </Text>
        </Grid.Col>

        {isWeightResponsible && !hasFinalAmountTask && (
          <Grid.Col span={12}>
            <Text size="sm" color={"primary"}>
              {t("orders:finalAmountHasBeenSet")}
            </Text>
          </Grid.Col>
        )}
        {!isWeightResponsible && order.status !== "AWAITING_FINAL_AMOUNT" && order.status !== "REQUIRES_ACTION" && (
          <Grid.Col span={12}>
            <Text size="sm" color={"primary"}>
              {t("orders:finalAmountHasBeenSet")}
            </Text>
          </Grid.Col>
        )}
        {!isWeightResponsible && (order.status === "AWAITING_FINAL_AMOUNT" || order.status === "REQUIRES_ACTION") && (
          <Grid.Col span={12}>
            <Text size="sm" color={"warning"}>
              {t("orders:weightResponsibleNeedsToSetFinalAmount")}
            </Text>
          </Grid.Col>
        )}
        {order.finalAmountConfirmedDespiteWarning && (
          <>
            <Grid.Col span={5}>
              <Text size="sm" color={"secondary"}>
                {t("orders:quantityCheck.title")}
              </Text>
            </Grid.Col>
            <Grid.Col span={7}>
              <QuantityCheckValue />
            </Grid.Col>
          </>
        )}
        {isWeightResponsible && hasFinalAmountTask && children}
      </Grid>
    </Accordion>
  );
};
